/** @jsxImportSource @emotion/react */
import { Paper as MuiPaper, useMediaQuery } from '@mui/material';
import { breakpoints, Buttons, TextFields } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { BasePage, DenseButton, Sidebars } from 'components';
import { useMerchantData } from 'contexts';
import { PageRoute, PermissionEnum } from 'enums';
import { css, theme } from 'global';
import { CustomersTableModule, InviteCustomerDialogModule } from 'modules';
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageProps } from 'types';

import * as styles from './CustomersPage.styles';

const CustomersPage: FC<PageProps> = ({ innerDrawerCallback }) => {
  const { checkPermission } = useMerchantData();
  const { status, consumerId } = useParams<{
    status: string;
    consumerId: string;
  }>();
  const location = useLocation() as unknown as Location & {
    state: { consumerId: string };
  };
  const navigate = useNavigate();
  const isSearchingCliRequest = location?.pathname.includes('/cli-invite');
  const [isInviteCustomerDialogOpen, setIsInviteCustomerDialogOpen] =
    useState<boolean>(false);

  const isMobile = useMediaQuery(breakpoints.down(theme.breaks.sm + 1));

  const customersTableRef = useRef();
  const sidebarRef = useRef();

  const [searchValue, setSearchValue] = useState<string>(
    location?.state?.consumerId ?? consumerId ?? ''
  );
  const [selectedConsumerId, setSelectedConsumerId] = useState<number>(
    Number(consumerId) ?? null
  );

  const handleSearchValueChange = (e): void => setSearchValue(e?.target?.value);

  const handleInviteCustomerClick = (): void =>
    isSearchingCliRequest
      ? navigate(`${PageRoute.Tools}${PageRoute.CliInvite}`)
      : setIsInviteCustomerDialogOpen(true);

  const handleSidebarClose = (): void => {
    const newPath = status
      ? `${PageRoute.Customers}/status/${status}`
      : PageRoute.Customers;
    navigate(newPath);
    setSelectedConsumerId(null);
  };

  // listen to escape key to close sidebar
  useEffect(function setupListener() {
    function closeSidebar(e: { code: string }): void {
      if (consumerId && e.code === `Escape`) {
        handleSidebarClose();
      }
    }
    document.addEventListener('keyup', closeSidebar);

    return (): void => document.removeEventListener('keyup', closeSidebar);
  }, []);

  // Button to invite customer that is moved for mobile view
  const inviteCustomerButton = (
    <Buttons.Secondary
      css={styles.inviteCustomerButton(!!selectedConsumerId)}
      onClick={handleInviteCustomerClick}
    >
      <Icons.Paperplane width={20} height={20} />
      <span id="buttonLabel">
        {isSearchingCliRequest ? 'Increase limit' : 'Invite customer'}
      </span>
    </Buttons.Secondary>
  );

  return (
    <BasePage
      title={isSearchingCliRequest ? 'Credit limit requests' : 'Customers'}
      variant="layout"
      fullWidth
      fullHeight
      flex
      action={isMobile && inviteCustomerButton}
    >
      <MuiPaper
        elevation={0}
        css={styles.ordersTableContainer(!!selectedConsumerId)}
      >
        <div id="toolbar" css={styles.toolbar}>
          <span>
            <TextFields.Outlined
              id="searchValue"
              placeholder="Search..."
              value={searchValue}
              onChange={handleSearchValueChange}
              css={styles.input}
              startIcon={<Icons.Search />}
              endIcon={
                searchValue && (
                  <Icons.Close
                    css={css.clearIcon}
                    onClick={handleSearchValueChange}
                  />
                )
              }
              fullWidth={isMobile}
            />
            {isMobile && (
              <DenseButton
                id="searchStatusFilter"
                ButtonIcon={status ? Icons.Check : Icons.Filter}
                onClick={innerDrawerCallback}
                fullWidth
              >
                Filter statuses
              </DenseButton>
            )}
          </span>
          {!isMobile &&
            checkPermission(PermissionEnum.CreateOrderExecute) &&
            inviteCustomerButton}
        </div>
        <CustomersTableModule
          ref={customersTableRef}
          searchValue={searchValue}
          selectedConsumerId={selectedConsumerId}
          setSelectedConsumerId={setSelectedConsumerId}
        />
      </MuiPaper>

      <InviteCustomerDialogModule
        open={isInviteCustomerDialogOpen}
        onClose={(): void => setIsInviteCustomerDialogOpen(false)}
      />

      <Sidebars.Customer
        ref={sidebarRef}
        consumerId={selectedConsumerId}
        onClose={handleSidebarClose}
      />
    </BasePage>
  );
};

export default CustomersPage;
